import type { LucideIcon } from "lucide-react";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

export interface PageTab {
  value: string;
  label: string;
  disabled?: boolean;
  href?: string;
}

/**
 * An action on the entity the page shows (duplicate, delete, archive, …).
 * Rendered as a menu in the context header: the sidebar on desktop, the
 * top header on mobile, the panel header in the side panel.
 */
export interface ContextAction {
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  destructive?: boolean;
  /** Disabled while offline (see DropdownMenuItem `offlineEdit`). */
  offlineEdit?: boolean;
  disabled?: boolean;
  testId?: string;
}

interface PageHeaderState {
  title: string | null;
  /**
   * Extra location trail entries after the context's active section, e.g. a
   * quote and its version (M2). Only for pages inside a context.
   */
  crumbs: string[];
  tabs: PageTab[];
  currentTab: string | null;
  onTabChange: ((value: string) => void) | null;
}

interface PageHeaderContextType extends PageHeaderState {
  setPageHeader: (state: PageHeaderState) => void;
  clearPageHeader: () => void;
  actions: ContextAction[];
  setActions: (actions: ContextAction[]) => void;
}

const EMPTY_STATE: PageHeaderState = {
  title: null,
  crumbs: [],
  tabs: [],
  currentTab: null,
  onTabChange: null,
};

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(
  undefined,
);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageHeaderState>(EMPTY_STATE);
  const [actions, setActions] = useState<ContextAction[]>([]);

  const setPageHeader = useCallback((next: PageHeaderState) => {
    setState(next);
  }, []);

  const clearPageHeader = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  return (
    <PageHeaderContext.Provider
      value={{
        ...state,
        setPageHeader,
        clearPageHeader,
        actions,
        setActions,
      }}
    >
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeaderContext() {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) {
    throw new Error(
      "usePageHeaderContext must be used within a PageHeaderProvider",
    );
  }
  return ctx;
}
